import React, { useState } from 'react';
import { useAdminAuth } from '../context/AdminAuthContext';
import { API_BASE } from '../../utils/getImageUrl';
import { ShoppingCart, Search, Plus, Trash2, Store, X, PackagePlus } from 'lucide-react';

const Orders = () => {
  const { orders = [], updateOrderStatus, fetchOrders, token } = useAdminAuth();
  const [searchTerm, setSearchTerm] = useState('');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [customerName, setCustomerName] = useState('Mağaza Satışı');
  const [customerPhone, setCustomerPhone] = useState('+994500000000');
  const [deliveryAddress, setDeliveryAddress] = useState('Mağazadan Təhvil');
  const [orderSource, setOrderSource] = useState('STORE_OFFLINE');

  const [productName, setProductName] = useState('');
  const [productPrice, setProductPrice] = useState('');
  const [productSize, setProductSize] = useState('M');
  const [productQuantity, setProductQuantity] = useState(1);

  const [selectedItems, setSelectedItems] = useState([]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('az-AZ', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const filteredOrders = orders.filter((order) => {
    const name = (order.customerName || order.customer || '').toLowerCase();
    const phone = (order.customerPhone || '').toLowerCase();
    const idStr = String(order.id || order.orderNumber || '').toLowerCase();
    const term = searchTerm.toLowerCase();
    return name.includes(term) || phone.includes(term) || idStr.includes(term);
  });

  const sourceLabel = (src) =>
    src === 'STORE_OFFLINE' ? 'Mağaza' : src === 'WHATSAPP_MANUAL' ? 'WhatsApp' : src === 'INSTAGRAM' ? 'Instagram' : 'Sayt';

  const statusLabel = (status) => {
    const s = (status || 'PENDING').toUpperCase();
    if (s === 'PENDING') return 'Gözləmədə';
    if (s === 'CONFIRMED') return 'Təsdiqli';
    if (s === 'CANCELLED') return 'Ləğv edilib';
    return 'Çatdırıldı';
  };

  const statusClass = (status) => {
    const s = (status || 'PENDING').toUpperCase();
    if (s === 'PENDING') return 'bg-amber-50 text-amber-700 border-amber-200';
    if (s === 'CONFIRMED') return 'bg-blue-50 text-blue-700 border-blue-200';
    if (s === 'CANCELLED') return 'bg-red-50 text-red-700 border-red-200';
    return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  };

  const handleAddDynamicProduct = () => {
    if (!productName.trim()) {
      alert('Zəhmət olmasa məhsulun adını daxil edin!');
      return;
    }
    if (!productPrice || Number(productPrice) <= 0) {
      alert('Zəhmət olmasa düzgün qiymət daxil edin!');
      return;
    }

    setSelectedItems([
      ...selectedItems,
      {
        productName: productName.trim(),
        price: Number(productPrice),
        quantity: Number(productQuantity) || 1,
        size: productSize,
        color: '',
      },
    ]);

    setProductName('');
    setProductPrice('');
    setProductSize('M');
    setProductQuantity(1);
  };

  const handleQuantityChange = (index, qty) => {
    const updated = [...selectedItems];
    updated[index].quantity = Math.max(1, Number(qty) || 1);
    setSelectedItems(updated);
  };

  const handleRemoveItem = (index) => {
    setSelectedItems(selectedItems.filter((_, i) => i !== index));
  };

  const calculateTotal = () =>
    selectedItems.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const handleCreateManualOrder = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (selectedItems.length === 0) {
      alert('Zəhmət olmasa ən azı bir məhsul əlavə edin!');
      return;
    }

    const payload = {
      customerName,
      customerPhone,
      deliveryAddress,
      orderSource,
      status: 'CONFIRMED',
      items: selectedItems.map((item) => ({
        productName: item.productName,
        price: item.price,
        quantity: item.quantity,
        size: item.size,
        color: item.color,
      })),
    };

    setSubmitting(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      const authToken = token || localStorage.getItem('adminToken');
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;

      // API_BASE onsuz da /api ilə bitir — bir daha əlavə etmirik
      const response = await fetch(`${API_BASE}/orders`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        alert('Sifariş uğurla yaradıldı!');
        setIsModalOpen(false);
        setSelectedItems([]);
        if (fetchOrders) fetchOrders();
      } else {
        let message = 'Xəta baş verdi. Məlumatları düzgün daxil etdiyinizdən əmin olun.';
        try {
          const data = await response.json();
          if (data?.error) message = data.error;
        } catch {
          /* JSON deyil — default mesaj qalsın */
        }
        alert(message);
      }
    } catch (err) {
      console.error('Sifariş əlavə edilərkən xəta:', err);
      alert('Şəbəkə xətası baş verdi.');
    } finally {
      setSubmitting(false);
    }
  };

  const StatusSelect = ({ order, className = '' }) => (
    <select
      value={(order.status || 'PENDING').toUpperCase()}
      onChange={(e) => updateOrderStatus(order.id, e.target.value)}
      className={`bg-white border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-black cursor-pointer font-medium ${className}`}
    >
      <option value="PENDING">Gözləmədə</option>
      <option value="CONFIRMED">Təsdiqlə</option>
      <option value="DELIVERED">Çatdırıldı</option>
      <option value="CANCELLED">Ləğv et</option>
    </select>
  );

  return (
    <div className="space-y-6 sm:space-y-8">
      <div className="space-y-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-serif text-gray-900">
            Sifarişlərin İdarəedilməsi
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            L'Aliel butikinə gələn bütün sifarişlərin siyahısı və status nəzarəti.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 sm:max-w-xs">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Müştəri, telefon və ya ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-black bg-white"
            />
          </div>

          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="flex items-center justify-center gap-2 bg-black hover:bg-gray-800 active:bg-gray-900 text-white text-sm font-medium px-4 py-2.5 rounded-xl transition-all shadow-sm whitespace-nowrap"
          >
            <Plus className="w-4 h-4" /> Manual Sifariş
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4 sm:p-6 border-b border-gray-100">
          <h2 className="text-base sm:text-lg font-serif font-bold text-gray-900 flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-gray-700 flex-shrink-0" />
            Sifariş Siyahısı ({filteredOrders.length})
          </h2>
        </div>

        {filteredOrders.length === 0 ? (
          <p className="text-center py-8 text-gray-400 text-sm">Heç bir sifariş tapılmadı.</p>
        ) : (
          <>
            {/* ---------- MOBİL ---------- */}
            <div className="lg:hidden divide-y divide-gray-100">
              {filteredOrders.map((order) => {
                const customer = order.customerName || order.customer || 'Anonim';
                const amount = Number(order.totalAmount ?? order.total ?? 0).toFixed(2);

                return (
                  <div key={order.id} className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate">{customer}</p>
                        {order.customerPhone && (
                          <a
                            href={`tel:${order.customerPhone}`}
                            className="text-xs text-blue-600 underline"
                          >
                            {order.customerPhone}
                          </a>
                        )}
                      </div>
                      <span
                        className={`px-2.5 py-1 rounded-full text-[11px] font-medium border flex-shrink-0 ${statusClass(
                          order.status
                        )}`}
                      >
                        {statusLabel(order.status)}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap text-xs">
                      <span className="font-mono text-gray-500">
                        #{order.id || order.orderNumber}
                      </span>
                      <span className="px-2 py-0.5 rounded bg-gray-100 font-medium text-gray-600">
                        {sourceLabel(order.orderSource)}
                      </span>
                      <span className="text-gray-500">
                        {formatDate(order.createdAt || order.date)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between gap-3">
                      <span className="font-semibold text-gray-950">
                        {amount} {order.currency || '₼'}
                      </span>
                      <StatusSelect order={order} className="text-sm flex-1 max-w-[160px]" />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ---------- DESKTOP ---------- */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 text-xs font-semibold uppercase border-b border-gray-100">
                    <th className="p-4 pl-6">Sifariş ID</th>
                    <th className="p-4">Müştəri Adı / Tel</th>
                    <th className="p-4">Mənbə</th>
                    <th className="p-4">Tarix</th>
                    <th className="p-4">Ümumi Məbləğ</th>
                    <th className="p-4">Cari Status</th>
                    <th className="p-4 text-center">Statusu Dəyiş</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 text-sm text-gray-700">
                  {filteredOrders.map((order) => {
                    const customer = order.customerName || order.customer || 'Anonim';
                    const amount = Number(order.totalAmount ?? order.total ?? 0).toFixed(2);

                    return (
                      <tr key={order.id} className="hover:bg-gray-50/70 transition-colors">
                        <td className="p-4 pl-6 font-mono text-xs text-gray-900 font-semibold">
                          #{order.id || order.orderNumber}
                        </td>
                        <td className="p-4 font-medium">
                          {customer}
                          {order.customerPhone && (
                            <span className="block text-xs font-normal text-gray-500">
                              {order.customerPhone}
                            </span>
                          )}
                        </td>
                        <td className="p-4">
                          <span className="text-xs px-2 py-0.5 rounded bg-gray-100 font-medium text-gray-600">
                            {sourceLabel(order.orderSource)}
                          </span>
                        </td>
                        <td className="p-4 text-gray-500 text-xs">
                          {formatDate(order.createdAt || order.date)}
                        </td>
                        <td className="p-4 font-semibold text-gray-950">
                          {amount} {order.currency || '₼'}
                        </td>
                        <td className="p-4">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-medium inline-block border ${statusClass(
                              order.status
                            )}`}
                          >
                            {statusLabel(order.status)}
                          </span>
                        </td>
                        <td className="p-4 text-center">
                          <StatusSelect order={order} className="text-xs shadow-sm" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* ---------- MANUAL SİFARİŞ MODALI ---------- */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-[80] flex items-end sm:items-center justify-center sm:p-4 backdrop-blur-sm">
          <div
            className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col max-h-[92vh] sm:max-h-[90vh]"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          >
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-100 flex-shrink-0">
              <h3 className="text-lg sm:text-xl font-serif font-bold text-gray-900 flex items-center gap-2">
                <Store className="w-5 h-5 text-gray-700 flex-shrink-0" /> Manual Sifariş
              </h3>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                aria-label="Bağla"
                className="flex items-center justify-center w-10 h-10 -mr-2 text-gray-400 hover:text-black rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form
              onSubmit={handleCreateManualOrder}
              className="flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6 space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Mənbə</label>
                  <select
                    value={orderSource}
                    onChange={(e) => setOrderSource(e.target.value)}
                    className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-black bg-white"
                  >
                    <option value="STORE_OFFLINE">Mağaza Satışı (Fiziki)</option>
                    <option value="WHATSAPP_MANUAL">WhatsApp Satışı</option>
                    <option value="INSTAGRAM">Instagram Satışı</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Müştəri Adı
                  </label>
                  <input
                    type="text"
                    required
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-black"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Telefon Nömrəsi
                  </label>
                  <input
                    type="tel"
                    inputMode="tel"
                    required
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-black"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Çatdırılma Ünvanı
                  </label>
                  <input
                    type="text"
                    required
                    value={deliveryAddress}
                    onChange={(e) => setDeliveryAddress(e.target.value)}
                    className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-black"
                  />
                </div>
              </div>

              {/* Sərbəst məhsul */}
              <div className="p-3 sm:p-4 border border-gray-200 bg-gray-50/70 rounded-2xl space-y-3">
                <div className="flex items-center gap-2 text-xs font-bold text-gray-700 uppercase">
                  <PackagePlus className="w-4 h-4 text-black" /> Sərbəst Məhsul Daxil Et
                </div>

                <input
                  type="text"
                  placeholder="Məhsul adı (məs: İpək Don)"
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  className="w-full p-3 border border-gray-200 bg-white rounded-xl text-sm focus:outline-none focus:border-black"
                />

                <div className="grid grid-cols-3 gap-2">
                  <input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    placeholder="Qiymət ₼"
                    value={productPrice}
                    onChange={(e) => setProductPrice(e.target.value)}
                    className="p-3 border border-gray-200 bg-white rounded-xl text-sm focus:outline-none focus:border-black"
                  />
                  <select
                    value={productSize}
                    onChange={(e) => setProductSize(e.target.value)}
                    className="p-3 border border-gray-200 bg-white rounded-xl text-sm focus:outline-none focus:border-black"
                  >
                    <option value="XS">XS</option>
                    <option value="S">S</option>
                    <option value="M">M</option>
                    <option value="L">L</option>
                    <option value="XL">XL</option>
                    <option value="XXL">XXL</option>
                    <option value="Standard">Std</option>
                  </select>
                  <input
                    type="number"
                    inputMode="numeric"
                    min="1"
                    placeholder="Say"
                    value={productQuantity}
                    onChange={(e) => setProductQuantity(e.target.value)}
                    className="p-3 border border-gray-200 bg-white rounded-xl text-sm text-center font-semibold focus:outline-none focus:border-black"
                  />
                </div>

                <button
                  type="button"
                  onClick={handleAddDynamicProduct}
                  className="w-full bg-black hover:bg-gray-800 active:bg-gray-900 text-white text-sm font-bold py-3 rounded-xl transition-all"
                >
                  + Əlavə Et
                </button>
              </div>

              {/* Əlavə olunmuş məhsullar */}
              {selectedItems.length > 0 && (
                <div className="border border-gray-200 rounded-xl p-3 space-y-2 bg-white">
                  <span className="text-xs font-semibold text-gray-500 uppercase block mb-2">
                    Sifariş Məhsulları ({selectedItems.length})
                  </span>

                  {selectedItems.map((item, idx) => (
                    <div
                      key={idx}
                      className="bg-gray-50 p-3 rounded-xl border border-gray-100 space-y-2"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <span className="font-semibold text-gray-900 block text-sm truncate">
                            {item.productName}
                          </span>
                          <span className="text-xs text-gray-500">
                            Ölçü: {item.size} · {item.price} ₼
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(idx)}
                          aria-label="Sil"
                          className="flex items-center justify-center w-9 h-9 -mt-1 -mr-1 text-red-500 hover:text-red-700 flex-shrink-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">Say:</span>
                          <input
                            type="number"
                            inputMode="numeric"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => handleQuantityChange(idx, e.target.value)}
                            className="w-16 p-2 border border-gray-200 text-center rounded-lg text-sm font-bold bg-white"
                          />
                        </div>
                        <span className="font-bold text-gray-900">
                          {(item.price * item.quantity).toFixed(2)} ₼
                        </span>
                      </div>
                    </div>
                  ))}

                  <div className="flex justify-between items-center pt-3 font-bold text-gray-900 border-t border-gray-200 mt-2">
                    <span className="text-sm">Cəmi Məbləğ:</span>
                    <span className="text-xl text-black font-serif">
                      {calculateTotal().toFixed(2)} ₼
                    </span>
                  </div>
                </div>
              )}

              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-3 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
                >
                  Ləğv Et
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-5 py-3 text-sm font-semibold bg-black hover:bg-gray-800 text-white rounded-xl transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Yaradılır...' : 'Sifarişi Yarat'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
